// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { CreationInfo, OctoChainData, OctoRule } from "./types";

/** Parse time string "HH:MM" to minutes from midnight */
export function timeToMinutes(t: string | undefined | null): number | null {
  if (!t || t.includes("store.")) return null;
  const parts = t.split(":").map(Number);
  return (parts[0] ?? 0) * 60 + (parts[1] ?? 0);
}

/** Minutes to percentage of the visible time range */
export function minutesToPercent(min: number, openMin: number, closeMin: number): number {
  const range = closeMin <= openMin ? 1440 - openMin + closeMin : closeMin - openMin;
  const offset = min < openMin ? 1440 - openMin + min : min - openMin;
  return (offset / range) * 100;
}

/** Format time for bar labels: "08:00" → "8h", "08:30" → "8h30" */
export function formatTime(t: string | undefined): string {
  if (!t) return "";
  return t.replace(":00", "h").replace(":", "h");
}

/** Types immune to workability rules */
export const W3_IMMUNE_TYPES = new Set(["ZIN", "PSI", "Obstruction", "SCHEDULED_ROTATION", "TAG_COMPLIANCE"]);

/** Check if a task type can be created in a department */
export function getCreationInfo(data: OctoChainData, deptCode: string, taskType: string): CreationInfo {
  if (data.globally_blocked_creation?.includes(taskType)) {
    return { allowed: false, windows: [], ruleId: null, note: "Globally blocked" };
  }

  const creationRules = data.rules.filter((r) => {
    if (r.layer !== "creation") return false;
    const matchesDept = !r.departments || r.departments.includes(deptCode) || r.departments.includes("*");
    const matchesType = r.applies_to
      ? r.applies_to.includes(taskType)
      : r.task_type === taskType || r.task_type === "*";
    return matchesDept && matchesType;
  });

  const blockRule = creationRules.find((r) => r.blocked);
  if (blockRule) {
    return { allowed: false, windows: [], ruleId: blockRule.id, note: blockRule.note ?? null };
  }

  const windowedRules = creationRules.filter((r) => r.blocked_outside_window && r.windows?.length > 0);
  if (windowedRules.length > 0) {
    let resultStart = timeToMinutes(data.store_hours.open) || 0;
    let resultEnd = timeToMinutes(data.store_hours.close) || 1440;
    let primaryRule = windowedRules[0]!;
    const notes: string[] = [];

    for (const r of windowedRules) {
      const validWindows = r.windows.filter((w) => timeToMinutes(w.start) !== null && timeToMinutes(w.end) !== null);
      for (const w of validWindows) {
        const ws = timeToMinutes(w.start)!;
        const we = timeToMinutes(w.end)!;
        resultStart = Math.max(resultStart, ws);
        resultEnd = Math.min(resultEnd, we);
      }
      if (r.note) notes.push(r.note);
      if (r.task_type !== "*") primaryRule = r;
    }

    if (resultStart < resultEnd) {
      const h = (n: number) => String(Math.floor(n / 60)).padStart(2, "0");
      const m = (n: number) => String(n % 60).padStart(2, "0");
      return {
        allowed: true,
        windows: [{ start: `${h(resultStart)}:${m(resultStart)}`, end: `${h(resultEnd)}:${m(resultEnd)}` }],
        ruleId: primaryRule.id,
        note: notes.join(" + "),
      };
    }
    return {
      allowed: false,
      windows: [],
      ruleId: primaryRule.id,
      note: "No valid creation window (rules overlap to zero)",
    };
  }

  return { allowed: true, windows: [], ruleId: null, note: null };
}

/** Get matching rules for a dept x type */
export function getMatchingRules(data: OctoChainData, deptCode: string, taskType: string): OctoRule[] {
  return data.rules.filter((rule) => {
    const matchesDept = !rule.departments || rule.departments.includes(deptCode) || rule.departments.includes("*");
    const matchesType = rule.applies_to
      ? rule.applies_to.includes(taskType)
      : rule.task_type === taskType || rule.task_type === "*";
    return matchesDept && matchesType;
  });
}

/** Get workability rules for a dept+type combo */
export function getWorkabilityInfo(data: OctoChainData, deptCode: string, taskType: string): OctoRule[] {
  if (W3_IMMUNE_TYPES.has(taskType)) return [];

  const specificRules = data.rules.filter((r) => {
    if (r.layer !== "workability") return false;
    if (r.task_type === "*") return false;
    const matchesDept = !r.departments || r.departments.includes(deptCode) || r.departments.includes("*");
    return matchesDept && r.task_type === taskType;
  });

  const w3 = data.rules.find((r) => r.layer === "workability" && r.task_type === "*");
  const defaultRules = w3 ? [w3] : [];

  return specificRules.length > 0 ? specificRules.concat(defaultRules) : defaultRules;
}
